import { useEffect, useRef, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { mediaManager } from '../../services/mediaManager'
import { useLivekitStore } from '../../stores/livekitStore'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore'
import { useToastStore } from '../../stores/toastStore'
import { useAuthStore } from '../../stores/authStore'
import { authApi } from '../../services/api'
import {
  ENHANCE_MIN,
  ENHANCE_MAX,
  resolveEnhance,
  writeEnhance,
  anyEnhanceOn,
  type EnhanceLevels,
} from '../../services/cameraEnhance'

const STEP = 5
/** Persist debounce — avoid spamming /me on every slider tick (mirrors CameraZoomPicker). */
const PERSIST_DELAY_MS = 600

/** Zoom-style split controls — one row per effect. */
const CONTROLS: { key: keyof EnhanceLevels; label: string }[] = [
  { key: 'smooth', label: 'Touch up my appearance' },
  { key: 'lowlight', label: 'Adjust for low light' },
  { key: 'vibrance', label: 'Vibrance' },
]

/**
 * Camera enhancement (OSPROD-109 v2) — three independent Zoom-style sliders:
 * skin smoothing, low-light lift, and vibrance. Each drives its own uniform in
 * the WebGL EnhanceProcessor stage. Persisted to the user's PROFILE
 * (`settings.cameraEnhance` — cross-device/room, OSPROD-232) with localStorage
 * as the offline cache; applies live to the running camera pipeline. Mirrors
 * CameraZoomPicker's popover shape.
 */
export default function CameraEnhancePicker({ compact = false }: { compact?: boolean } = {}) {
  const channelRoom = useLivekitStore((s) => s.room)
  const dmRoom = useDmCallStore((s) => s.room)
  const room = channelRoom ?? dmRoom
  const user = useAuthStore((s) => s.user)
  const [open, setOpen] = useState(false)
  const [levels, setLevels] = useState<EnhanceLevels>(() => resolveEnhance(user?.settings))
  const ref = useRef<HTMLDivElement>(null)
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Apply the persisted levels to the live pipeline on mount / room change
  // (profile value wins; localStorage is the offline/legacy fallback), but
  // only when they differ from what mediaManager already has — mounting the
  // menu must not force a needless CombinedProcessor rebuild (same guard as
  // CameraZoomPicker, OSPROD-232).
  useEffect(() => {
    const l = resolveEnhance(user?.settings)
    setLevels(l)
    const cur = mediaManager.getVideoEnhance() // 0..1
    const target = { smooth: l.smooth / ENHANCE_MAX, lowlight: l.lowlight / ENHANCE_MAX, vibrance: l.vibrance / ENHANCE_MAX }
    const differs =
      Math.abs(cur.smooth - target.smooth) > 0.001 ||
      Math.abs(cur.lowlight - target.lowlight) > 0.001 ||
      Math.abs(cur.vibrance - target.vibrance) > 0.001
    if (differs) mediaManager.setEnhanceLevels(room, target).catch(() => {})
  }, [room, user?.id])

  // Cleanup any in-flight persist timer on unmount.
  useEffect(() => () => {
    if (persistTimer.current) clearTimeout(persistTimer.current)
  }, [])

  // Save to the user's profile (debounced). First save from a device that
  // only had localStorage levels naturally migrates them up — `next` is built
  // from the resolved local state.
  const persist = (next: EnhanceLevels) => {
    if (persistTimer.current) clearTimeout(persistTimer.current)
    persistTimer.current = setTimeout(() => {
      const merged = {
        ...(user?.settings ?? {}),
        cameraEnhance: next,
      }
      authApi.updateMe({ settings: merged } as any)
        .then((res) => useAuthStore.setState({ user: res.data }))
        .catch(() => {})
    }, PERSIST_DELAY_MS)
  }

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const handleErr = (err: any) => {
    useToastStore.getState().showToast({
      id: `enhance-fail-${Date.now()}`,
      type: 'error',
      title: "Couldn't apply enhancement",
      body: err?.message ?? 'Unknown error',
    })
  }

  const apply = (key: keyof EnhanceLevels, v: number) => {
    const next = { ...levels, [key]: v }
    setLevels(next)
    writeEnhance(next) // device-local cache (offline fallback)
    mediaManager
      .setEnhanceLevels(room, {
        smooth: next.smooth / ENHANCE_MAX,
        lowlight: next.lowlight / ENHANCE_MAX,
        vibrance: next.vibrance / ENHANCE_MAX,
      })
      .catch(handleErr)
    persist(next)
  }

  const isActive = anyEnhanceOn(levels)
  const sz = compact ? 'w-8 h-8' : 'w-10 h-10'
  const iconSize = compact ? 14 : 20
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={isActive ? 'Camera enhancement on — click to adjust' : 'Enhance appearance'}
        className={`${sz} rounded-full flex items-center justify-center transition duration-150 ${
          isActive ? 'bg-blue-600/80 text-white hover:bg-blue-600' : 'bg-muted text-foreground hover:bg-muted/80'
        }`}
      >
        <Sparkles size={iconSize} />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 right-0 w-72 bg-card rounded-lg ring-1 ring-border shadow-lg overflow-hidden z-30">
          <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
            Enhance appearance
          </div>
          <div className="p-3 space-y-3">
            {CONTROLS.map(({ key, label }) => (
              <div key={key}>
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="text-foreground">{label}</span>
                  <span className="text-muted-foreground tabular-nums">{levels[key]}%</span>
                </div>
                <input
                  type="range"
                  min={ENHANCE_MIN}
                  max={ENHANCE_MAX}
                  step={STEP}
                  value={levels[key]}
                  onChange={(e) => apply(key, Number(e.target.value))}
                  className="w-full accent-primary"
                  aria-label={label}
                />
              </div>
            ))}
          </div>
          <div className="px-3 py-2 text-[10px] text-muted-foreground border-t border-border">
            Applied locally — peers see the enhanced video. Composes with background blur & zoom.
          </div>
        </div>
      )}
    </div>
  )
}
